import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Eye, ExternalLink, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminBlog() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login?next=/admin/blog");
  }, [user, loading, navigate]);

  const refresh = () => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/blog/posts").then(({ data }) => setPosts(data || []));
  };

  useEffect(() => { refresh(); }, [user]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      toast.success("Post deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    } finally { setBusy(false); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div data-testid="admin-blog-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-cm-accent font-medium">
          <ChevronLeft className="w-4 h-4" /> Back to admin
        </Link>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">Blog management</h1>
            <p className="text-sm text-slate-500 mt-1">Create and publish content to grow organic traffic.</p>
          </div>
          <Link to="/admin/blog/new" data-testid="new-post-btn" className="px-5 py-3 rounded-xl bg-cm-primary hover:bg-cm-accent text-white font-medium flex items-center gap-2 press-scale">
            <Plus className="w-4 h-4" /> New post
          </Link>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {posts.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              No posts yet. <Link to="/admin/blog/new" className="text-cm-accent font-medium">Create your first post →</Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-post-${p.id}`} className="flex items-center gap-4 p-4">
                  <div className="w-20 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-cm-primary truncate">{p.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="capitalize">{p.category}</span> · {p.tags?.join(", ") || "no tags"} · slug: <code className="text-[10px]">{p.slug}</code>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500"><Eye className="w-3 h-3" /> {p.views || 0}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {p.status}
                  </span>
                  {p.status === "published" && (
                    <Link to={`/blog/${p.slug}`} data-testid={`view-post-${p.id}`} target="_blank" rel="noreferrer" className="hidden md:inline-flex w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 items-center justify-center" title="View live">
                      <ExternalLink className="w-3.5 h-3.5 text-slate-600" />
                    </Link>
                  )}
                  <Link to={`/admin/blog/edit/${p.id}`} data-testid={`edit-post-${p.id}`} className="w-8 h-8 rounded-lg bg-cm-primary text-white flex items-center justify-center hover:bg-cm-accent" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </Link>
                  <button onClick={() => remove(p.id)} disabled={busy} data-testid={`delete-post-${p.id}`} className="w-8 h-8 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center disabled:opacity-50" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
